import { IPromise } from 'angular';
import { call, select } from 'redux-saga/effects';
import * as _ from 'underscore';

import { DatabasesQueryByIdsNodes } from '@tableau/data-catalog-client-components';
import { selectItemFromHomeState } from '../../../home/selectors/HomeSelectors';
import { deepClone } from '../../../ts/helpers/DeepClone';
import { Filter } from '../../../ts/helpers/Filter';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataConnectionInfo } from '../../../ts/models/IDataConnectionInfo';
import { IDataRoleInfo } from '../../../ts/models/IDataRoleInfo';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { IProjectInfo } from '../../../ts/models/IProjectInfo';
import { IPublishedConnectionInfo } from '../../../ts/models/IPublishedConnectionInfo';
import { IViewInfo } from '../../../ts/models/IViewInfo';
import { IWorkbookInfo } from '../../../ts/models/IWorkbookInfo';
import { databasesByIds } from '../../../ts/resources/DatabasesByIds';
import { DataConnections } from '../../../ts/resources/DataConnections';
import { DataRoles } from '../../../ts/resources/Dataroles';
import { DataSources } from '../../../ts/resources/Datasources';
import { Projects } from '../../../ts/resources/Projects';
import { PublishedConnections } from '../../../ts/resources/PublishedConnections';
import { Views } from '../../../ts/resources/Views';
import { Workbooks } from '../../../ts/resources/Workbooks';
import { DatasourceIdToConnectionIdsMap } from '../../../ts/ServerApi/types/DatasourceIdToConnectionIdsMap';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { IError } from '../../../ts/ServerApi/types/IError';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { MaxRequestArrayLength } from '../../../ts/services/ServerService';
import { makeItemsSet } from '../../ItemsSet';
import { isInCurrentSpace, selectContentFromAppState } from '../../selectors/ContentSelectors';
import { selectSpaceInfo, selectSpaceInfoItem } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { isHomeSpace } from '../../state/SpaceInfo';
import { WindowLocationService } from '../../../commonInfra/services/WindowLocationService';

export function isValidResult(result: IResult): boolean {
  return !result || !result.errors || result.errors.length === 0;
}

export function filterOutErroredItems(errors: IError[], items: IContentInfo[]): IContentInfo[] {
  if (!errors) {
    return items;
  }

  // Create set or errors with item id as the key.
  // Then filter out those items from given list of items
  let errorIds = makeItemsSet((error: IError) => error.id , ...errors);
  return _.filter(items, (item: IContentInfo) => !errorIds.hasOwnProperty(item.id));
}

export function getWorkbookInfos(workbooks: IContentInfo[]): IPromise<IWorkbookInfo[]> {
  const idArray = workbooks.map(c => c.id);
  const workbooksResource = ServiceLocator.get<Workbooks>('Workbooks');
  return getContentInfos(idArray, workbooksResource);
}

export function getProjectInfos(projects: IContentInfo[]): IPromise<IProjectInfo[]> {
  const idArray = projects.map(c => c.id);
  const projectsResource = ServiceLocator.get<Projects>('Projects');

  return getContentInfos(idArray, projectsResource);
}

export function getViewInfos(views: IContentInfo[]): IPromise<IViewInfo[]> {
  const idArray = views.map(c => c.id);
  const viewsResource = ServiceLocator.get<Views>('Views');
  return getContentInfos(idArray, viewsResource);
}

export function getDataRoleInfos(dataRoles: IContentInfo[]): IPromise<IDataRoleInfo[]> {
  const idArray = dataRoles.map(c => c.id);
  const dataRolesResource = ServiceLocator.get<DataRoles>('DataRoles');
  return getContentInfos(idArray, dataRolesResource);
}

export function getPublishedConnectionInfos(publishedConnections: IContentInfo[]): IPromise<IDataRoleInfo[]> {
  const idArray: string[] = publishedConnections.map(c => c.id);
  const publishedConnectionsResource = ServiceLocator.get<PublishedConnections>('PublishedConnections');
  return getContentInfos(idArray, publishedConnectionsResource);
}

export function getDatabases(databases: IContentInfo[]): IPromise<DatabasesQueryByIdsNodes[]> {
  const idArray = databases.map(c => c.id);
  return getContentInfos(idArray, databasesByIds);
}

function getContentInfos<TItem extends Array<IViewInfo | IProjectInfo | IWorkbookInfo | IDataRoleInfo | IPublishedConnectionInfo | DatabasesQueryByIdsNodes>>(idArray: string[], contentResource: any): IPromise<TItem>{
  if(idArray.length <= MaxRequestArrayLength) {
    return getContentInfoSlice(idArray, idArray.length, contentResource);
  }

  const $q = ServiceLocator.get<ng.IQService>('$q');
  let idx: number = 0;
  const numSlices: number = Math.ceil(idArray.length / MaxRequestArrayLength);
  let outstandingPromises: IPromise<TItem>[] = [];

  for(let i = 0; i < numSlices; i++) {
    outstandingPromises.push(getContentInfoSlice(idArray.slice(idx, idx + MaxRequestArrayLength), MaxRequestArrayLength, contentResource));
    idx = idx + MaxRequestArrayLength;
  }

  return $q.all(outstandingPromises).then((resultArray: TItem[]) => {
    return _.flatten(resultArray) as TItem;
  });
}

function getContentInfoSlice<TItem>(idArray: string[], numElements: number, contentResource: any): IPromise<TItem>{
  return contentResource.all()
                      .addFilter(Filter.stringFieldInArray('id', idArray))
                      .getSlice(0, numElements)
                      .then((list: any) => {
                        return list.items;
                      });
}

export function getDatasourceInfos(datasources: IContentInfo[]): IPromise<IDataSourceInfo[]> {
  const datasourcesResource = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
  return datasourcesResource.fromContent(...datasources);
}

// find the matching entity item from the selected content
export function* findSelectedContent(entityId: string, entityType: ExploreContentApi_ContentType){
  const spaceInfo = yield select(selectSpaceInfo);
  if(isHomeSpace(spaceInfo)) {
    return yield select(selectItemFromHomeState, entityId, entityType);
  } else {
    const items = yield select(selectContentFromAppState);
    const item = _.find(items, (item: IContentInfo) => entityId === item.id);
    return item;
  }
}

// TODO: This is a hack and exists only because getDatasourceActions only supports data connection IDs
export function* getCorrectEntityIdForActions(entityType: ExploreContentApi_ContentType, entityId: string) {
  if (entityType === 'dataconnection') {
    const item = yield call(findSelectedContent, entityId, entityType);
    // we need the datasource id to grab the actions for a dataconnection
    return (item as IDataConnectionInfo).datasource.id;
  }
  else if (entityType === 'datasource') {
    const inCurrentSpace = yield select(isInCurrentSpace, { id: entityId, spaceType: entityType });
    const item = inCurrentSpace ?
      yield select(selectSpaceInfoItem) : yield call(findSelectedContent, entityId, entityType);

    if(DataSources.isUnifiedDatasource(item)) {
      return item.id;
    }
    const id = yield call(getDataConnectionId, entityId);
    return id || entityId;
  }

  return entityId;
}

export function* getDataConnectionId(datasourceId: string) {
  return yield getDataConnectionIds([ datasourceId ])
    .then((map: DatasourceIdToConnectionIdsMap) => map[datasourceId] ? map[datasourceId][0] : null);
}

export function getDataConnectionIds(datasourceIds: string[]): ng.IPromise<DatasourceIdToConnectionIdsMap> {
  if(datasourceIds.length > 0) {
    const dataConnectionsResource = ServiceLocator.get<DataConnections>('DataConnections');
    return dataConnectionsResource.getConnectionIdsFromDatasourceIds(datasourceIds);
  } else {
    return Q.when({}) as ng.IPromise<DatasourceIdToConnectionIdsMap>;
  }
}

export function replaceDatasourceIdWithConnectionIdForMixedContent(contentArray: ReadonlyArray<Readonly<IContentInfo>>): ng.IPromise<IContentInfo[]> {
  const unifiedDatasources: IContentInfo[] = [];
  const contentInfoDatasources: IContentInfo[] = [];
  contentArray.forEach(function(contentElement) {
    if (contentElement.contentType === 'datasource' && !DataSources.isUnifiedDatasource(contentElement)) {
      contentInfoDatasources.push(deepClone(contentElement));
    } else {
      unifiedDatasources.push(deepClone(contentElement));
    }
  });

  return getDataConnectionIds(contentInfoDatasources.map(i => i.id))
    .then((datasourceIdToDataConnectionIdsMap: DatasourceIdToConnectionIdsMap) => {
      const contentInfoDatasourcesWithUnifiedDatasourceIds =
        contentInfoDatasources.map(contentElement => {
          const dataConnectionIds = datasourceIdToDataConnectionIdsMap[ contentElement.id ];
          return { ...contentElement, id: dataConnectionIds[ 0 ] };
        });
      return contentInfoDatasourcesWithUnifiedDatasourceIds.concat(unifiedDatasources);
    });
}

export function openBlankWindow(): Window {
  return WindowLocationService.open('');
}

export function setWindowLocation(window: Window, location: string): void {
  window.location.href = location;
}

export function getDatasourceDetailInfo(datasourceId: string): ng.IPromise<IDataSourceDetailInfo> {
  const datasourcesResource = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
  return datasourcesResource.getById(datasourceId);
}
